<script lang="ts">
  import MultiSelect from "$app/components/MultiSelect.svelte"
  import { User } from "lucide-svelte"
  import { toast, xhr } from "$app/utils"
  import { isLeadership } from "$app/utils/goals.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    goal: GoalType
    onupdate: (data: GoalType) => void
  }

  let { goal = $bindable(), onupdate }: Props = $props()

  const plan = appStore.plan

  const editable: boolean = appStore.editable || appStore.update_goals

  const currentDepartment = appStore.assignees.find(
    (d) => d.id === goal.assignee_id
  )

  let users: UserType[] = appStore.users

  let options: UserType[] = $state([])
  let selectedUsers = users.filter((op) => {
    return goal.user_ids.includes(op.id)
  })

  if (goal?.visibility) {
    options = users.filter(
      (u) => !u.deleted && !currentDepartment.user_ids.includes(u.id)
    )
  }

  function allowVisible(opt: any): boolean {
    if (goal.visibility === "leadership") {
      return isLeadership(opt.role)
    } else {
      return true
    }
  }

  let user_ids = goal.user_ids
  function handleSelect(detail): void {
    user_ids = detail.map((d) => d.id).sort()
  }

  let department_users = $derived(
    users.filter(
      (u) => !u.deleted && currentDepartment?.user_ids?.includes(u.id)
    )
  )

  let saving = $state(false)
  async function updateUserGoals(): Promise<void> {
    saving = true

    try {
      const resp = await xhr.patch(`/plans/${plan.id}/goals/${goal.id}.json`, {
        goal: { user_ids }
      })
      toast("Successfully updated members for goal", "success")
      user_ids = goal.user_ids
      goal = { ...goal, ...resp.data }
      onupdate(resp.data)
    } catch (e) {
      toast("Failed to update members for goal", "error")
    } finally {
      saving = false
    }
  }
</script>

<h3 class="mt-2">
  List of members assigned to
  <span class="font-semibold">{goal.assignee_name}</span>
  department:
</h3>

<div
  class="flex items-center w-full py-1 flex-wrap py-3 px-4 border-l border-dashed border-gray-200 mt-1"
>
  {#each department_users as user}
    <div
      class="flex items-center py-1.5 px-3 border border-gray-100 rounded-lg mr-1 shadow text-sm"
    >
      <img
        src={user.avatar_thumb_url}
        alt=""
        class="size-6 rounded-full mr-2"
      />
      <span>{user.name}</span>
    </div>
  {:else}
    <div
      class="py-3 px-4 border rounded border-gray-200 mt-1 text-sm italic w-full"
    >
      No members have been assigned to the
      <span class="font-semibold">{goal.assignee_name}</span> department.
    </div>
  {/each}
</div>

{#if editable}
  <h3 class="flex items-center mb-2 mt-6 text-base">
    Individual members assigned to this goal:
  </h3>

  <MultiSelect
    {options}
    searchField="name"
    valueField="id"
    placeholder="Type to find members"
    onselect={handleSelect}
    selected={selectedUsers}
  >
    {#snippet selectedSnippet(option, idx)}
      <img
        src={option.avatar_thumb_url}
        alt={option.name}
        class="w-8 h-8 rounded-full inline-block mr-2 border-2"
        class:border-blue-500={isLeadership(option.role)}
      />
      <span>{option.name}</span>
    {/snippet}
    {#snippet optionSnippet(option, idx)}
      <div>
        <div class="flex py-2 px-4" class:hidden={!allowVisible(option)}>
          <img
            src={option.avatar_thumb_url}
            alt=""
            class="w-8 h-8 rounded-full mr-2 border-2"
            class:border-blue-500={isLeadership(option.role)}
          />
          <span>
            {option.name}
          </span>
        </div>
      </div>
    {/snippet}
  </MultiSelect>

  <button class="btn primary mt-2" onclick={updateUserGoals} disabled={saving}>
    Update Goal Members
  </button>
{/if}
